import { toastr } from 'react-redux-toastr';
import i18n from 'i18next';
import { sensorsConstants } from './sensors.constants';
import { axiosInstance, ENDPOINT } from '../../../common/constants';
import { handleResponse } from '../../../common/helpers';

/**
 * Get event configuration metadata
 * Fetches enum mappings and metadata for event configuration UI
 */
export const getEventConfigMetadata = () => (dispatch, getState) => {
  // Check if already loaded
  const { eventMetadata } = getState().sensors.details;
  if (eventMetadata && eventMetadata.data) {
    return Promise.resolve(eventMetadata.data);
  }

  const request = () => ({ type: sensorsConstants.GET_EVENT_METADATA_REQUEST });
  const success = data => ({ type: sensorsConstants.GET_EVENT_METADATA_SUCCESS, data });
  const failure = error => ({ type: sensorsConstants.GET_EVENT_METADATA_FAILURE, error });

  dispatch(request());
  return handleResponse(axiosInstance.get(ENDPOINT.GET_EVENT_METADATA)).then(
    (res) => {
      dispatch(success(res));
      return res;
    },
    (error) => {
      dispatch(failure(error));
      const errorMessage = error && error.message ? `: ${error.message}` : '';
      toastr.error(i18n.t('devices.eventDetection.toastr.metadataLoadFailed', {
        defaultValue: `Failed to load event configuration metadata${errorMessage}`
      }));
      throw error;
    }
  );
};

/**
 * Get alarm thresholds for a location
 * Fetches alarm thresholds from tags for event configuration coupling
 * @param {number} locationId - ID of the location
 * @param {number} unitSystem - Unit system (1 for metric, 2 for imperial)
 */
export const getAlarmThresholds = (locationId, unitSystem = 1) => (dispatch) => {
  const request = () => ({ type: sensorsConstants.GET_ALARM_THRESHOLDS_REQUEST });
  const success = data => ({ type: sensorsConstants.GET_ALARM_THRESHOLDS_SUCCESS, data });
  const failure = error => ({ type: sensorsConstants.GET_ALARM_THRESHOLDS_FAILURE, error });

  const unitSystemParam = unitSystem === 2 ? 'imperial' : 'metric';

  dispatch(request());
  return handleResponse(axiosInstance.get(`${ENDPOINT.GET_ALARM_THRESHOLDS(locationId)}?unit_system=${unitSystemParam}`)).then(
    (res) => {
      dispatch(success(res));
      return res;
    },
    (error) => {
      dispatch(failure(error));
      // Don't show toastr error - let the component handle it gracefully
      // Return empty thresholds structure on error
      const emptyThresholds = {
        temperature: null,
        vibration: {},
        available_bins: [],
        missing: { temperature: true, vibration: {} }
      };
      dispatch(success(emptyThresholds));
      return emptyThresholds;
    }
  );
};

/**
 * Check if location can use couple-with-temperature-dashboard on event rules.
 */
export const getDashboardCouplingEligibility = locationId => () => handleResponse(
  axiosInstance.get(ENDPOINT.GET_DASHBOARD_COUPLING_ELIGIBILITY(locationId))
).then(res => res, () => ({ eligible: false, reason: 'error' }));
